// Ciro Talepleri rapor modali — ceyrek + ekip multi-select, secime gore
// APPROVED kayitlari Excel (.xlsx) olarak indirir. CiroTalepleriPage'de inline duran
// 95+ satirlik JSX + handler'lar buraya tasindi.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { downloadXLSX } from '../../../utils/xlsxExport';
import { revenueProjectNumber } from '../../../utils/revenueProjectNumber';

// Hook data'sinda eksik kalan durumlar icin UI fallback sozlugu — Hasura
// `ciro_kanali_label` doner; islemlerimiz tipik olarak onu kullanir.
const CIRO_KANALI_LABEL = {
  SATIS_CIROSU: 'Satış Cirosu',
  PROJE_YAZIM_CIROSU: 'Proje Yazım Cirosu',
  ONAY_SONRASI_CIROSU: 'Onay Sonrası Cirosu',
};

// Mevcut + onceki yilin 4 ceyregi (yakin tarih ustte). Sayfa-spesifik bir
// helper oldugu icin component dosyasinda duruyor.
const buildQuarterOptions = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const years = [currentYear, currentYear - 1];
  const options = [];
  years.forEach((y) => {
    [1, 2, 3, 4].forEach((q) => {
      options.push({ key: `${y}-Q${q}`, yil: y, ceyrek: q, label: `${y}/Q${q}` });
    });
  });
  return options.sort((a, b) => {
    if (a.yil !== b.yil) return b.yil - a.yil;
    return b.ceyrek - a.ceyrek;
  });
};

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Array} props.entries - userRevenueEntries (tam liste, status filter
 *   modal icinde APPROVED'a indirgenir)
 * @param {Array<{ id: any, name: string }>} props.teams - ekran datasindan
 *   turetilen ekipler
 * @param {Array} props.bonusRevenueTypes
 * @param {Map<any, number>} props.coefficientById - tutar * coef = hedef ciro
 */
const CiroRaporModal = ({
  open,
  onClose,
  entries,
  teams,
  bonusRevenueTypes,
  coefficientById,
}) => {
  const { t } = useTranslation();

  const quarterOptions = useMemo(() => buildQuarterOptions(), []);
  const [reportQuarters, setReportQuarters] = useState([]);
  const [reportTeams, setReportTeams] = useState([]);

  // Modal her acildiginda secimleri "tum ceyrekler + tum ekipler" olarak
  // resetle — UX'i CiroTalepleriPage'deki orijinal davranisla ayni tutuyoruz.
  useEffect(() => {
    if (!open) return;
    setReportQuarters(quarterOptions.map((q) => q.key));
    setReportTeams((teams || []).map((tm) => tm.id));
  }, [open, quarterOptions, teams]);

  const teamNameById = useMemo(() => {
    const m = new Map();
    (teams || []).forEach((tm) => m.set(tm.id, tm.name));
    return m;
  }, [teams]);

  const typeNameById = useMemo(() => {
    const m = new Map();
    (bonusRevenueTypes || []).forEach((b) => {
      m.set(b.id, b.name || `#${b.id}`);
    });
    return m;
  }, [bonusRevenueTypes]);

  // Spec'teki 10 sutun. CSV satirlarinda camelCase interim row field'lari
  // kullaniyoruz (`__` prefix yerine).
  const reportColumns = useMemo(
    () => [
      { label: t('userRevenueEntries.report.columns.quarter', 'Çeyrek'),
        value: (r) => r.quarterLabel },
      { label: t('userRevenueEntries.report.columns.team', 'Ekip'),
        value: (r) => r.teamName },
      { label: t('userRevenueEntries.report.columns.person', 'Kişi'),
        value: (r) => r.personName },
      { label: t('userRevenueEntries.report.columns.firm', 'Firma'),
        value: (r) => r.firmName },
      { label: t('userRevenueEntries.report.columns.job', 'İş Başlığı'),
        value: (r) => r.jobTitle },
      { label: t('userRevenueEntries.report.columns.contract', 'Sözleşme Başlığı'),
        value: (r) => r.contractLabel },
      { label: t('userRevenueEntries.report.columns.channel', 'Ciro Kanalı'),
        value: (r) => r.channelLabel },
      { label: t('userRevenueEntries.report.columns.type', 'Ciro Türü'),
        value: (r) => r.typeName },
      { label: t('userRevenueEntries.report.columns.projectNo', 'Proje No'),
        value: (r) => r.projectNo },
      { label: t('userRevenueEntries.report.columns.amount', 'Tutar'),
        value: (r) => r.amount },
      { label: t('userRevenueEntries.report.columns.targetAmount', 'Hedefe Esas Ciro'),
        value: (r) => r.targetAmount },
      { label: t('userRevenueEntries.report.columns.description', 'Açıklama'),
        value: (r) => r.description },
    ],
    [t],
  );

  const handleDownload = useCallback(() => {
    const list = Array.isArray(entries) ? entries : [];
    const quarterSet = new Set(reportQuarters);
    const teamSet = new Set(reportTeams);

    const rows = list
      .filter((row) => row?.status === 'APPROVED')
      .filter((row) => {
        const key = `${row.yil}-Q${row.ceyrek}`;
        return quarterSet.size === 0 ? false : quarterSet.has(key);
      })
      .filter((row) => {
        const teamId = row.user?.team_id ?? row.team_id ?? null;
        return teamSet.size === 0 ? false : teamSet.has(teamId);
      })
      .map((row) => {
        const coef = coefficientById?.get?.(row.bonus_revenue_type_id) ?? 1;
        const tutar = Number(row.tutar || 0);
        const teamId = row.user?.team_id ?? row.team_id ?? null;
        return {
          quarterLabel: `${row.yil}/Q${row.ceyrek}`,
          teamName:
            row.user?.team?.name ||
            teamNameById.get(teamId) ||
            (teamId ? `#${teamId}` : '-'),
          personName:
            [row.user?.first_name, row.user?.last_name].filter(Boolean).join(' ') ||
            (row.user_id ? `#${row.user_id}` : '-'),
          firmName:
            row.firm?.firm_short_name || row.firm?.firm_name || row.firm_name || '-',
          jobTitle: row.job?.job_title || row.job_label || '-',
          contractLabel: row.contract_label
            || (row.contract
              ? [row.contract?.contract_subject?.name, row.contract?.scope_of_work?.name]
                  .filter(Boolean)
                  .join(' / ')
                || `Sözleşme #${row.contract?.id}`
              : '-'),
          channelLabel:
            row.ciro_kanali_label ||
            CIRO_KANALI_LABEL[row.ciro_kanali] ||
            row.ciro_kanali ||
            '-',
          typeName:
            typeNameById.get(row.bonus_revenue_type_id) ||
            (row.bonus_revenue_type_id ? `#${row.bonus_revenue_type_id}` : '-'),
          projectNo: revenueProjectNumber(row) || '-',
          amount: tutar,
          targetAmount: tutar * coef,
          description: row.aciklama || '',
        };
      });

    const now = new Date();
    const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const baseName = t('userRevenueEntries.report.fileName', 'ciro-raporu');
    // Excel (.xlsx): sayilar sayi hucresi, CSV'deki virgullu metin degil.
    downloadXLSX(rows, reportColumns, `${baseName}-${stamp}.xlsx`);
    onClose();
  }, [
    entries,
    reportQuarters,
    reportTeams,
    coefficientById,
    teamNameById,
    typeNameById,
    reportColumns,
    t,
    onClose,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('userRevenueEntries.report.modalTitle', 'Ciro Raporu')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-quarters-label">
              {t('userRevenueEntries.report.quartersLabel', 'Çeyrekler')}
            </InputLabel>
            <Select
              labelId="report-quarters-label"
              multiple
              value={reportQuarters}
              onChange={(e) => {
                const v = e.target.value;
                setReportQuarters(typeof v === 'string' ? v.split(',') : v);
              }}
              input={
                <OutlinedInput
                  label={t('userRevenueEntries.report.quartersLabel', 'Çeyrekler')}
                />
              }
              renderValue={(selected) =>
                quarterOptions
                  .filter((q) => selected.includes(q.key))
                  .map((q) => q.label)
                  .join(', ')
              }
            >
              {quarterOptions.map((q) => (
                <MenuItem key={q.key} value={q.key}>
                  <Checkbox checked={reportQuarters.includes(q.key)} />
                  <ListItemText primary={q.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="report-teams-label">
              {t('userRevenueEntries.report.teamsLabel', 'Ekipler')}
            </InputLabel>
            <Select
              labelId="report-teams-label"
              multiple
              value={reportTeams}
              onChange={(e) => {
                const v = e.target.value;
                setReportTeams(typeof v === 'string' ? v.split(',') : v);
              }}
              input={
                <OutlinedInput
                  label={t('userRevenueEntries.report.teamsLabel', 'Ekipler')}
                />
              }
              renderValue={(selected) =>
                (teams || [])
                  .filter((tm) => selected.includes(tm.id))
                  .map((tm) => tm.name)
                  .join(', ')
              }
            >
              {(teams || []).map((tm) => (
                <MenuItem key={tm.id} value={tm.id}>
                  <Checkbox checked={reportTeams.includes(tm.id)} />
                  <ListItemText primary={tm.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('userRevenueEntries.report.cancel', 'İptal')}
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon fontSize="small" />}
          disabled={reportQuarters.length === 0 || reportTeams.length === 0}
        >
          {t('userRevenueEntries.report.download', 'İndir')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CiroRaporModal;
